// Local city landing pages — trilingual config (Phoenix, Scottsdale, Fort Lauderdale, Montréal).
// Each city reuses the shared MiamiFlow renderer; per-city overrides sit on top of the
// language defaults from miamiConfig{,Fr,Es}.js. Service-area honesty rule: only Phoenix
// (Ocean Data LLC) and Montréal (Ocean Data Inc.) are stated as offices; Scottsdale and
// Fort Lauderdale are described as markets served from those offices.

import { MIAMI_EN, INDUSTRIES, PEOPLE_BUCKETS, CAPACITY_QUESTIONS, CAPACITY_EXAMPLE, getIndustry } from "./miamiConfig";
import { MIAMI_FR, INDUSTRIES_FR, PEOPLE_BUCKETS_FR, CAPACITY_QUESTIONS_FR, CAPACITY_EXAMPLE_FR, getIndustryFr } from "./miamiConfigFr";
import { MIAMI_ES, INDUSTRIES_ES, PEOPLE_BUCKETS_ES, CAPACITY_QUESTIONS_ES, CAPACITY_EXAMPLE_ES, getIndustryEs } from "./miamiConfigEs";

const FLOW = {
  en: { industries: INDUSTRIES, buckets: PEOPLE_BUCKETS, questions: CAPACITY_QUESTIONS, example: CAPACITY_EXAMPLE, getIndustryFn: getIndustry, base: MIAMI_EN },
  fr: { industries: INDUSTRIES_FR, buckets: PEOPLE_BUCKETS_FR, questions: CAPACITY_QUESTIONS_FR, example: CAPACITY_EXAMPLE_FR, getIndustryFn: getIndustryFr, base: MIAMI_FR },
  es: { industries: INDUSTRIES_ES, buckets: PEOPLE_BUCKETS_ES, questions: CAPACITY_QUESTIONS_ES, example: CAPACITY_EXAMPLE_ES, getIndustryFn: getIndustryEs, base: MIAMI_ES },
};

const CITIES = {
  phoenix: {
    market: "phoenix",
    en: {
      docTitle: "Phoenix Business Process Automation — HVAC, Plumbing, Construction | Ocean Data",
      eyebrow: "Ocean Data · Phoenix, Arizona",
      heroLine1: "Give your Phoenix team back the time",
      heroSub:
        "Ocean Data LLC is based in Phoenix, at 300 W. Clarendon Ave. We help Arizona construction, HVAC, plumbing, property management and service businesses connect the tools they already use — Zoho, QuickBooks, Microsoft 365, Autodesk — so work flows from first contact to final payment without re-entry or chasing.",
    },
    fr: {
      docTitle: "Automatisation des processus d'affaires à Phoenix — CVAC, plomberie, construction | Ocean Data",
      eyebrow: "Ocean Data · Phoenix, Arizona",
      heroLine1: "Redonnez à votre équipe de Phoenix le temps",
      heroSub:
        "Ocean Data LLC est basée à Phoenix, au 300 W. Clarendon Ave. On aide les entreprises de construction, CVAC, plomberie, gestion immobilière et services de l'Arizona à connecter leurs outils actuels — Zoho, QuickBooks, Microsoft 365, Autodesk — pour que le travail coule du premier contact au dernier paiement.",
    },
    es: {
      docTitle: "Automatización de procesos de negocio en Phoenix — HVAC, plomería, construcción | Ocean Data",
      eyebrow: "Ocean Data · Phoenix, Arizona",
      heroLine1: "Devuélvale a su equipo de Phoenix el tiempo",
      heroSub:
        "Ocean Data LLC está en Phoenix, en 300 W. Clarendon Ave. Ayudamos a empresas de construcción, HVAC, plomería, administración de propiedades y servicios de Arizona a conectar las herramientas que ya usan — Zoho, QuickBooks, Microsoft 365, Autodesk — para que el trabajo fluya del primer contacto al pago final.",
    },
  },
  scottsdale: {
    market: "scottsdale",
    en: {
      docTitle: "Scottsdale Field Service & Property Management Automation | Ocean Data",
      eyebrow: "Ocean Data · Scottsdale, Arizona",
      heroLine1: "Give your Scottsdale team back the time",
      heroSub:
        "Ocean Data serves Scottsdale from our Phoenix office. We help Scottsdale property management, facilities, HVAC, plumbing and construction teams replace re-typed work orders and scattered spreadsheets with one connected system — measured in hours returned, not software sold.",
    },
    fr: {
      docTitle: "Automatisation des services sur le terrain à Scottsdale | Ocean Data",
      eyebrow: "Ocean Data · Scottsdale, Arizona",
      heroLine1: "Redonnez à votre équipe de Scottsdale le temps",
      heroSub:
        "Ocean Data dessert Scottsdale depuis notre bureau de Phoenix. On aide les équipes de gestion immobilière, d'installations, de CVAC, de plomberie et de construction de Scottsdale à remplacer la ressaisie et les tableurs éparpillés par un seul système connecté — mesuré en heures rendues, pas en logiciels vendus.",
    },
    es: {
      docTitle: "Automatización de servicios de campo en Scottsdale | Ocean Data",
      eyebrow: "Ocean Data · Scottsdale, Arizona",
      heroLine1: "Devuélvale a su equipo de Scottsdale el tiempo",
      heroSub:
        "Ocean Data atiende Scottsdale desde nuestra oficina de Phoenix. Ayudamos a equipos de administración de propiedades, instalaciones, HVAC, plomería y construcción de Scottsdale a reemplazar la reescritura y las hojas de cálculo dispersas por un solo sistema conectado — medido en horas devueltas, no en software vendido.",
    },
  },
  "fort-lauderdale": {
    market: "fort-lauderdale",
    en: {
      docTitle: "Fort Lauderdale HVAC, Plumbing & Construction Workflow Integration | Ocean Data",
      eyebrow: "Ocean Data · Fort Lauderdale, South Florida",
      heroLine1: "Give your Fort Lauderdale team back the time",
      heroSub:
        "Ocean Data serves Fort Lauderdale and South Florida. We help construction, HVAC, plumbing and property management businesses connect sales, field work and accounting into one flow — in English or Spanish — without replacing the tools your team already knows.",
    },
    fr: {
      docTitle: "Intégration des flux de travail CVAC, plomberie et construction à Fort Lauderdale | Ocean Data",
      eyebrow: "Ocean Data · Fort Lauderdale, Floride du Sud",
      heroLine1: "Redonnez à votre équipe de Fort Lauderdale le temps",
      heroSub:
        "Ocean Data dessert Fort Lauderdale et le sud de la Floride. On aide les entreprises de construction, CVAC, plomberie et gestion immobilière à connecter ventes, terrain et comptabilité en un seul flux — sans remplacer les outils que votre équipe connaît déjà.",
    },
    es: {
      docTitle: "Integración de flujos de trabajo HVAC, plomería y construcción en Fort Lauderdale | Ocean Data",
      eyebrow: "Ocean Data · Fort Lauderdale, Sur de la Florida",
      heroLine1: "Devuélvale a su equipo de Fort Lauderdale el tiempo",
      heroSub:
        "Ocean Data atiende Fort Lauderdale y el sur de la Florida. Ayudamos a empresas de construcción, HVAC, plomería y administración de propiedades a conectar ventas, trabajo de campo y contabilidad en un solo flujo — en inglés o español — sin reemplazar las herramientas que su equipo ya conoce.",
    },
  },
  montreal: {
    market: "montreal",
    en: {
      docTitle: "Montréal Business Process Automation — Construction & Services | Ocean Data",
      eyebrow: "Ocean Data · Montréal, Québec",
      heroLine1: "Give your Montréal team back the time",
      heroSub:
        "Ocean Data Inc. is based in Montréal, at 75 Rue Queen. We help Québec construction, professional-services and field-service businesses connect Zoho, QuickBooks, Microsoft 365 and the tools they already run into one calm system — in French or English.",
    },
    fr: {
      docTitle: "Automatisation des processus d'affaires à Montréal — construction et services | Ocean Data",
      eyebrow: "Ocean Data · Montréal, Québec",
      heroLine1: "Redonnez à votre équipe de Montréal le temps",
      heroSub:
        "Ocean Data inc. est basée à Montréal, au 75 Rue Queen. On aide les entreprises de construction, de services professionnels et de services sur le terrain du Québec à connecter Zoho, QuickBooks, Microsoft 365 et leurs outils actuels en un seul système calme — en français ou en anglais.",
    },
    es: {
      docTitle: "Automatización de procesos de negocio en Montreal — construcción y servicios | Ocean Data",
      eyebrow: "Ocean Data · Montreal, Québec",
      heroLine1: "Devuélvale a su equipo de Montreal el tiempo",
      heroSub:
        "Ocean Data Inc. está en Montreal, en 75 Rue Queen. Ayudamos a empresas de construcción, servicios profesionales y servicios de campo de Québec a conectar Zoho, QuickBooks, Microsoft 365 y sus herramientas actuales en un solo sistema tranquilo — en francés, inglés o español.",
    },
  },
};

export const getCityContent = (city, lang) => {
  const flow = FLOW[lang];
  const cityData = CITIES[city];
  if (!flow || !cityData || !cityData[lang]) return null;
  return {
    industries: flow.industries,
    buckets: flow.buckets,
    questions: flow.questions,
    example: flow.example,
    getIndustryFn: flow.getIndustryFn,
    copy: { ...flow.base, ...cityData[lang] },
    market: cityData.market,
    source: lang === "en" ? `/${city}` : `/${lang}/${city}`,
  };
};
